import { FC, useMemo } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { calculateTotalPositiveDueAmount, calculateTotalOpenAmount } from '../../../utils/invoice-amount.utils';

type Props = {
   currency: string;
   openInvoices: Transaction[];
   filteredOpenInvoices: Transaction[];
   areOpenInvoicesFiltered: boolean;
};

export const InvoiceSummary: FC<Props> = ({ currency, openInvoices, filteredOpenInvoices, areOpenInvoicesFiltered }) => {
   const translate = useTranslations();
   const format = useFormatter();
   const { desktop } = useBreakpoint();

   const responsiveDirection = desktop ? 'row' : 'column';
   const responsiveGap = desktop ? '3xl' : 'md';

   const totals = useMemo(() => {
      const totalAmount = calculateTotalOpenAmount(openInvoices);
      const totalOverDue = calculateTotalPositiveDueAmount(openInvoices);

      return {
         totalAmount,
         totalOverDue
      };
   }, [openInvoices]);

   const filteredTotals = useMemo(() => {
      const totalAmount = calculateTotalOpenAmount(filteredOpenInvoices);
      const totalOverDue = calculateTotalPositiveDueAmount(filteredOpenInvoices);

      return {
         totalAmount,
         totalOverDue
      };
   }, [filteredOpenInvoices]);

   return (
      <Box justify="between" width="100%" direction={responsiveDirection}>
         <Box gap={responsiveGap} direction={responsiveDirection}>
            <Box direction="column" gap="none">
               <Box gap="2xs">
                  <Typography variant="body-highlight" color="strong">
                     {areOpenInvoicesFiltered
                        ? translate('invoices.invoice-summary.open-amount')
                        : translate('invoices.invoice-summary.total-open-amount')}
                  </Typography>
                  <Tooltip align="start" pointers text={translate('invoices.open-invoices.billing-notification')}>
                     <Box>
                        <Icon icon="info-circle" />
                     </Box>
                  </Tooltip>
               </Box>
               {areOpenInvoicesFiltered ? (
                  <>
                     <Typography variant="heading-4" color="strong">
                        {format.price({ amount: filteredTotals.totalAmount, currency })}
                     </Typography>
                     {totals.totalAmount !== 0 && (
                        <Typography variant="caption" color="subtle">
                           {translate('invoices.invoice-summary.total-amount')}:{' '}
                           {format.price({
                              amount: totals.totalAmount,
                              currency
                           })}
                        </Typography>
                     )}
                  </>
               ) : (
                  <Typography variant="heading-4" color="strong">
                     {format.price({ amount: totals.totalAmount, currency })}
                  </Typography>
               )}
            </Box>
            {totals.totalOverDue !== 0 && (
               <Box direction="column" gap="none">
                  <Typography variant="body-highlight" color="error">
                     {translate('invoices.invoice-summary.overdue-title')}
                  </Typography>
                  {areOpenInvoicesFiltered ? (
                     <>
                        <Typography variant="heading-4" color="error">
                           {format.price({ amount: filteredTotals.totalOverDue, currency })}
                        </Typography>
                        <Typography variant="caption" color="error">
                           {translate('invoices.invoice-summary.total-overdue')}:{' '}
                           {format.price({
                              amount: totals.totalOverDue,
                              currency
                           })}
                        </Typography>
                     </>
                  ) : (
                     <Typography variant="heading-4" color="error">
                        {format.price({ amount: totals.totalOverDue, currency })}
                     </Typography>
                  )}
               </Box>
            )}
         </Box>
         <Box>
            <Typography color="subtle">
               {translate(`invoices.invoice-summary.number-of-invoices${filteredOpenInvoices.length === 1 ? '-singular' : ''}`, {
                  number: filteredOpenInvoices.length
               })}
            </Typography>
         </Box>
      </Box>
   );
};
